// FT-2 — web-rnw debt: reads the feature registry and answers two
// different questions with one pass.
//
//   1. WHAT IS THERE TO LIFT? → `pool`. Every feature whose web-rnw
//      surface is absent or partial. This is the thing the RNW lift never
//      had: consolidation has `pnpm deadcode` generating its work, the
//      lift had nothing, so it depended on someone remembering and it
//      quietly stopped. A registry query generates it instead.
//
//   1b. AND WHICH OF IT CAN A NIGHT ACTUALLY FINISH? → `runnable`, added
//      by FT-3 (2026-08-19). The pool answered "who owes web work" and the
//      nightly prompt read that as "what to lift" — two different
//      questions. The nights of 18-19/08 opened all 23 pool items one by
//      one and closed none: some need a web platform half WRITTEN (no
//      mobile code crosses), some are chapters, some were not behind
//      mobile at all. `runnable` is the pool minus those, and it is what
//      the CLI prints and what --json hands the night.
//      ⛔ `excluded` is NOT `n-a` and NOT an exemption from parity
//      (rnw-strangler.md §3.8): an excluded feature stays in `pool` and
//      still counts for --gate-stable. It leaves the running order, not
//      the ledger.
//
//   2. IS THIS DEBT NEW? → `unacknowledged`. A feature shipped today
//      without a web surface blocks the STABLE promotion; the features
//      that already carried the gap on the day the registry was populated
//      (68 of the 113, 2026-08-14) do not block anything. Blocking on the
//      baseline would stop every release tomorrow morning, which is how a
//      gate gets disabled instead of obeyed.
//
// The shape mirrors catalogValidation's exception mechanism (the one
// baseline pattern this repo already runs in production), including the
// part that matters most: `staleBaseline`. A baseline entry whose feature
// has since been lifted is reported, so the baseline SHRINKS as the
// nights work through the pool. Without it the debt stays forgiven
// forever and the file becomes a list of things nobody will ever fix.
//
// Shape validation is NOT redone here — validateFeatures owns that. The
// caller parses with FeatureRegistry first.

import type {
  FeatureEntry,
  FeatureRegistry,
  FeatureSurfaceStatus,
  LiftExclusionKind,
} from "./schemas/featureRegistry.js";
import type { ProfileRole } from "./schemas/profile.js";

/** One line of tooling/features/web-debt-baseline.json. */
export interface WebDebtBaselineEntry {
  id: string;
  reason: string;
}

/** A feature that owes work on web-rnw — one item of the lift pool. */
export interface WebDebtItem {
  id: string;
  name: string;
  areaId: string;
  areaName: string;
  surface: Extract<FeatureSurfaceStatus, "absent" | "partial">;
  roles: ProfileRole[];
  note?: string;
  /**
   * Ordering weight, high first. See `rankOf` — the area's declared
   * `liftPriority` dominates, then a lying `partial` mount, then
   * athlete-facing work. ⛔ Not a promise about difficulty: it says what is
   * worth doing first, never what is quick.
   */
  rank: number;
}

/**
 * The night's running order.
 *
 * Three terms, and only the first is a product judgement:
 *   - the area's declared `liftPriority` (0 when unset) — Antenore's knob,
 *     and it dominates by an order of magnitude so it is never outvoted by
 *     the two mechanical signals;
 *   - `partial` over `absent` — a mount that EXISTS AND LIES is worse than
 *     one that is missing, because nothing counting mounts can see it. The
 *     reference case is the web cardio session reading "0 sets · Volume 0 kg"
 *     while the set is right there;
 *   - athlete-facing over coach/admin — three real athletes use the athlete
 *     surfaces every day; the coach console has one user, who is also the
 *     person who can wait.
 */
const rankOf = (
  surface: "absent" | "partial",
  roles: readonly ProfileRole[],
  areaPriority: number,
): number =>
  areaPriority * 100 +
  (surface === "partial" ? 10 : 0) +
  (roles.includes("athlete") ? 5 : 0);

/**
 * Why a pool item is not the night's work. `design-gated` is read off the
 * feature's own lifecycle `status` and needs nobody to declare it; the
 * other three are declared per-entry (`liftExclusion`) and carry evidence.
 */
export type LiftBlockCause = "design-gated" | LiftExclusionKind;

/** A pool item that is debt, but not work a night can pick up. */
export interface ExcludedItem extends WebDebtItem {
  cause: LiftBlockCause;
  why: string;
}

/**
 * The one predicate that answers "can a night take this?", so the two
 * mechanical rules and the declared one stay in one testable place.
 *
 * ⛔ Order matters: `status` first. A feature still in design is blocked by
 * the decision, whatever else its entry says — and reading it off the
 * lifecycle enum means nobody has to remember to write it down, which is
 * the failure mode this whole file exists to remove.
 */
function liftBlockOf(
  feature: FeatureEntry,
): { cause: LiftBlockCause; why: string } | null {
  if (feature.status !== "shipped") {
    return {
      cause: "design-gated",
      why: `status is "${feature.status}" — the decision comes before the lift, and it is Antenore's`,
    };
  }
  if (feature.liftExclusion) {
    return {
      cause: feature.liftExclusion.kind,
      why: feature.liftExclusion.why,
    };
  }
  return null;
}

export interface StaleBaselineEntry {
  id: string;
  why: string;
}

/** A capability that only the retiring legacy web still carries. */
export interface LegacyOnlyItem {
  id: string;
  name: string;
  areaName: string;
  legacy: Extract<FeatureSurfaceStatus, "shipped" | "partial">;
}

export interface WebDebtResult {
  /** Every feature owing web work — baselined or not. The debt ledger. */
  pool: WebDebtItem[];
  /**
   * The pool minus what no night can finish — the running order the CLI
   * prints and --json hands the nightly agent. A subset of `pool`: the
   * excluded items are still debt, they just are not tonight's work.
   */
  runnable: WebDebtItem[];
  /** Pool items a night cannot take, each with its cause + evidence. */
  excluded: ExcludedItem[];
  /** Debt covered by the baseline: historical, blocks nothing. */
  baselined: WebDebtItem[];
  /** Debt NOT in the baseline: new, and it blocks the stable promotion. */
  unacknowledged: WebDebtItem[];
  /** Baseline entries that no longer describe real debt — prune them. */
  staleBaseline: StaleBaselineEntry[];
  /**
   * What we would LOSE by switching off apps/web: it is on legacy and on
   * neither of the surfaces that replace it. This is the answer that
   * rnw-strangler §8 keeps by hand — the standing rule says never retire
   * the old web while this list is non-empty, and a hand-kept checklist is
   * exactly what let the lift stop in the first place.
   */
  legacyOnly: LegacyOnlyItem[];
  /** True when nothing NEW owes web work. Never gates on the baseline. */
  ok: boolean;
}

export function analyzeWebDebt(
  registry: FeatureRegistry,
  baseline: WebDebtBaselineEntry[],
): WebDebtResult {
  const baselineIds = new Set(baseline.map((e) => e.id));
  const usedBaselineIds = new Set<string>();
  const knownIds = new Set<string>();

  const pool: WebDebtItem[] = [];
  const runnable: WebDebtItem[] = [];
  const excluded: ExcludedItem[] = [];
  const baselined: WebDebtItem[] = [];
  const unacknowledged: WebDebtItem[] = [];
  const legacyOnly: LegacyOnlyItem[] = [];

  for (const area of registry.areas) {
    for (const feature of area.features) {
      knownIds.add(feature.id);
      const surface = feature.surfaces["web-rnw"];

      const legacy = feature.surfaces["web-legacy"];
      if (
        (legacy === "shipped" || legacy === "partial") &&
        feature.surfaces.mobile === "absent" &&
        surface === "absent"
      ) {
        legacyOnly.push({
          id: feature.id,
          name: feature.name,
          areaName: area.name,
          legacy,
        });
      }

      // "n-a" is not debt: the surface cannot host it. "shipped" is done.
      if (surface !== "absent" && surface !== "partial") continue;

      // ⛔ You cannot LIFT what does not exist. The pool's whole job is "what
      // does mobile have that web-rnw does not", so a feature absent from
      // mobile too is not debt — it is unbuilt work, and it belongs to the
      // backlog, not to the night.
      //
      // Measured 2026-08-16: 18 of 67 pool items were in this state
      // (`planned` / `in_design` / `parked` features whose web surface is
      // trivially "absent" because nothing exists anywhere). The night agent
      // opens them, finds nothing to move, and burns the context that was
      // supposed to buy a lift. One of the 18 was created that same afternoon
      // by an honest registry entry for a calculation with no UI yet — which
      // is exactly why this has to be a predicate and not a habit of
      // whoever fills the yaml.
      if (
        feature.surfaces.mobile !== "shipped" &&
        feature.surfaces.mobile !== "partial"
      ) {
        continue;
      }

      const item: WebDebtItem = {
        id: feature.id,
        name: feature.name,
        areaId: area.id,
        areaName: area.name,
        surface,
        roles: feature.roles,
        note: feature.note,
        rank: rankOf(surface, feature.roles, area.liftPriority ?? 0),
      };
      pool.push(item);

      // FT-3 — debt either way; the split decides only who works on it.
      const block = liftBlockOf(feature);
      if (block) excluded.push({ ...item, ...block });
      else runnable.push(item);

      if (baselineIds.has(feature.id)) {
        usedBaselineIds.add(feature.id);
        baselined.push(item);
      } else {
        unacknowledged.push(item);
      }
    }
  }

  const staleBaseline: StaleBaselineEntry[] = baseline
    .filter((e) => !usedBaselineIds.has(e.id))
    .map((e) => ({
      id: e.id,
      why: knownIds.has(e.id)
        ? "feature no longer owes web work — lifted, or marked n-a"
        : "no feature with this id in the registry — renamed or removed",
    }));

  // Highest value first, and stable within a rank so two runs of the same
  // registry hand the night the same order. ⛔ The night reads this top-down
  // (`nightly-lift-prompt.md §1`): a pool printed in registry order is a flat
  // list, and a flat list plus "take the first you can close" is how four
  // nights in a row came back with the smallest items in the file.
  const byRank = (a: WebDebtItem, b: WebDebtItem) =>
    b.rank - a.rank || a.id.localeCompare(b.id);
  pool.sort(byRank);
  runnable.sort(byRank);
  // Excluded items are grouped by cause when printed, so the useful order
  // inside a group is alphabetical — rank says "do this first", and there
  // is nothing here to do first.
  excluded.sort((a, b) => a.cause.localeCompare(b.cause) || a.id.localeCompare(b.id));

  return {
    pool,
    runnable,
    excluded,
    baselined,
    unacknowledged,
    staleBaseline,
    legacyOnly,
    ok: unacknowledged.length === 0,
  };
}

/** Compact human-readable report for the CLI / gate failure message. */
export function formatWebDebtReport(r: WebDebtResult): string {
  const lines: string[] = [];
  lines.push(
    `web-rnw debt: ${r.pool.length} in pool · ${r.baselined.length} baselined · ${r.unacknowledged.length} new · ${r.staleBaseline.length} stale baseline entries`,
  );
  // "in pool" is the DEBT, not a to-do list: most of it is not work a night
  // can take. Said here because this text is read at the moment a release is
  // refused, and "23 in pool" reads as "23 things to fix" to whoever is
  // standing in front of the gate.
  lines.push(
    `  (${r.runnable.length} of those are takeable work — \`pnpm lift:pool\` splits them and says why)`,
  );
  for (const u of r.unacknowledged) {
    lines.push(`  NEW    ${u.id} [${u.surface}] ${u.name}`);
  }
  for (const s of r.staleBaseline) {
    lines.push(`  STALE  ${s.id} — ${s.why}`);
  }
  return lines.join("\n");
}

/**
 * What retiring apps/web would cost, for rnw-strangler §8. An empty list
 * is the condition the standing rule asks for before switching it off.
 */
export function formatLegacyOnly(r: WebDebtResult): string {
  if (r.legacyOnly.length === 0) {
    return "legacy-only: nothing — apps/web can be retired without losing a capability.";
  }
  const lines = [
    `legacy-only: ${r.legacyOnly.length} capabilities would be LOST if apps/web were retired today`,
  ];
  for (const item of r.legacyOnly) {
    lines.push(`  [${item.legacy}] ${item.id} — ${item.name} (${item.areaName})`);
  }
  return lines.join("\n");
}

/**
 * The lift pool IN RUNNING ORDER, for the nightly agent's prompt.
 *
 * ⛔ Ordered, not grouped by area. The grouping read nicely and cost a night
 * a week: the agent was handed a flat list and told to take the first item it
 * could finish, so it took the shortest one in the file. The order is now the
 * instruction, and the area only labels each line.
 */
export function formatLiftPool(r: WebDebtResult): string {
  const lines: string[] = [];

  if (r.runnable.length === 0) {
    lines.push(
      "lift pool: empty — nothing here is a night's work. That is a result, not a failure.",
    );
  } else {
    lines.push(
      `lift pool: ${r.runnable.length} features are takeable tonight — IN RUNNING ORDER, start at the top`,
    );
    for (const [i, item] of r.runnable.entries()) {
      // "partial" in the label: a mount that lies is worse than a mount that is
      // missing, because no audit counting mounts sees it.
      lines.push(
        `\n${String(i + 1).padStart(3)}. [${item.surface}] ${item.id}   (rank ${item.rank} · ${item.areaName})`,
      );
      lines.push(`      ${item.name}${item.note ? ` — ${item.note}` : ""}`);
    }
  }

  if (r.excluded.length > 0) lines.push(formatExcluded(r));
  return lines.join("\n");
}

/**
 * The other half of the truth: debt that is real and still owed, but that
 * no night can close. Printed under the running order rather than hidden,
 * because a filter nobody can see is how a pool starts lying again — and
 * because a `design-gated` line IS the queue of decisions waiting on
 * Antenore.
 *
 * Not exported: `formatLiftPool` is the only caller, and the barrel
 * (packages/shared/src/index.ts) re-exports this module whole.
 */
function formatExcluded(r: WebDebtResult): string {
  const byCause = new Map<LiftBlockCause, ExcludedItem[]>();
  for (const item of r.excluded) {
    const bucket = byCause.get(item.cause);
    if (bucket) bucket.push(item);
    else byCause.set(item.cause, [item]);
  }

  const lines = [
    `\n— ${r.excluded.length} more features owe web work but are NOT a night's job (they stay debt: --gate-stable still counts them) —`,
  ];
  for (const [cause, items] of byCause) {
    lines.push(`\n  ${cause} (${items.length}):`);
    for (const item of items) {
      lines.push(`    ${item.id}`);
      lines.push(`      ${item.why}`);
    }
  }
  return lines.join("\n");
}
